import { useEffect, useMemo, useRef } from 'react';
import { AdvancedMarker, APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import type { LatLng, RouteInfo, Restaurant } from '@bilmat/shared';
import { decodePolyline } from '../lib/polyline';
import { DetourPin } from './DetourPin';
import { LocateIcon } from './LocateIcon';
import { useApp } from '../state/store';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string;
// AdvancedMarker kräver ett Map-ID — utan eget (även tom sträng i .env) faller
// vi tillbaka på Googles demo-ID så restaurangnålarna alltid renderas.
const MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) || 'DEMO_MAP_ID';

type Props = {
  route: RouteInfo;
  restaurants: Restaurant[];
};

function LocationFab() {
  const map = useMap();
  const onClick = (): void => {
    if (!map || !('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        map.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        map.setZoom(12);
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };
  return (
    <button type="button" className="map-fab" aria-label="Visa min position" onClick={onClick}>
      <LocateIcon />
    </button>
  );
}

function RoutePolyline({ path }: { path: LatLng[] }) {
  const map = useMap();
  useEffect(() => {
    if (!map || path.length === 0) return;
    const styles = getComputedStyle(document.documentElement);
    const casing = new google.maps.Polyline({
      path,
      strokeColor: styles.getPropertyValue('--route-casing').trim() || '#fff',
      strokeWeight: 8,
      strokeOpacity: 1,
      zIndex: 1,
    });
    const brand = new google.maps.Polyline({
      path,
      strokeColor: styles.getPropertyValue('--brand').trim() || '#ec9a2c',
      strokeWeight: 5,
      strokeOpacity: 1,
      zIndex: 2,
    });
    casing.setMap(map);
    brand.setMap(map);
    return () => {
      casing.setMap(null);
      brand.setMap(null);
    };
  }, [map, path]);
  return null;
}

/**
 * Zoomar till restaurang-utsnittet (nålarna) hellre än hela rutten — det är
 * dem användaren scannar. Biasar nedåt-padding så klustret hamnar i den
 * synliga kartdelen ovanför bottensheetet. Faller tillbaka på hela rutten
 * när inga restauranger finns ännu.
 */
function FitBounds({ path, restaurants }: { path: LatLng[]; restaurants: Restaurant[] }) {
  const map = useMap();
  const prevKey = useRef<string | null>(null);
  useEffect(() => {
    if (!map) return;
    // Sortering/filtrering ger nya array-identiteter med samma nålar — hoppa
    // över omzoomningen då, annars kastas användarens panorering/zoom bort.
    const key =
      restaurants.length > 0
        ? restaurants.map((r) => r.placeId).sort().join('|')
        : `route:${path.length}:${path[0]?.lat ?? 0},${path[0]?.lng ?? 0}`;
    if (prevKey.current === key) return;
    prevKey.current = key;
    const pts = restaurants.length > 0 ? restaurants.map((r) => r.location) : path;
    if (pts.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 56);
    const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
      const z = map.getZoom();
      if (typeof z === 'number' && z > 13) map.setZoom(13);
    });
    return () => listener.remove();
  }, [map, path, restaurants]);
  return null;
}

export function MapView({ route, restaurants }: Props) {
  const theme = useApp((s) => s.theme);
  const selectedPlaceId = useApp((s) => s.selectedPlaceId);
  const selectRestaurant = useApp((s) => s.selectRestaurant);
  const path = useMemo(() => decodePolyline(route.overviewPolyline), [route.overviewPolyline]);
  const center = path[Math.floor(path.length / 2)] ?? { lat: 59.33, lng: 18.06 };

  return (
    <div className="map-view">
      <APIProvider apiKey={MAPS_KEY}>
        <Map
          mapId={MAP_ID}
          defaultCenter={center}
          defaultZoom={7}
          colorScheme={theme === 'dark' ? 'DARK' : 'LIGHT'}
          disableDefaultUI
          gestureHandling="greedy"
        >
          <RoutePolyline path={path} />
          <FitBounds path={path} restaurants={restaurants} />
          <LocationFab />
          {restaurants.map((r) => (
            <AdvancedMarker
              key={r.placeId}
              position={r.location}
              onClick={() => selectRestaurant(r.placeId)}
            >
              <DetourPin minutes={r.detourMinutes} selected={r.placeId === selectedPlaceId} />
            </AdvancedMarker>
          ))}
        </Map>
      </APIProvider>
    </div>
  );
}
